import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardBody, CardTitle, Row, Col, Button } from 'reactstrap';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import Pagination from '../Pagination';

const AmbassadorDetail = () => {
  const { ambassadorId } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [referredUsers, setReferredUsers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambassadorId]);

  useEffect(() => {
    if (ambassadorId) {
      fetchReferredUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambassadorId, currentPage]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE_URL}/admin/campus/ambassador-overview/${ambassadorId}/stats`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching ambassador stats:', err);
      setError(err.response?.data?.error || 'Failed to load ambassador statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferredUsers = async () => {
    try {
      setUsersLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE_URL}/admin/campus/ambassador-overview/${ambassadorId}/referred-users`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { page: currentPage, limit: 50 }
        }
      );
      setReferredUsers(response.data.users || []);
      setPagination(response.data.pagination || null);
    } catch (err) {
      console.error('Error fetching referred users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPerformanceColor = (trend) => {
    switch (trend) {
      case 'increasing':
        return '#16a34a'; // green
      case 'decreasing':
        return '#dc3545'; // red
      default:
        return '#64748b'; // gray
    }
  };

  const getPerformanceIcon = (trend) => {
    switch (trend) {
      case 'increasing':
        return '↗';
      case 'decreasing':
        return '↘';
      default:
        return '→';
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        <div className="mt-3">
          <Button color="secondary" onClick={() => navigate('/campus/ambassador-overview')}>
            Back to Ambassador Overview
          </Button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="alert alert-info" role="alert">
        No data available for this ambassador.
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <Button
            color="link"
            onClick={() => navigate('/campus/ambassador-overview')}
            className="p-0 mb-2"
            style={{ textDecoration: 'none' }}
          >
            ← Back to Ambassador Overview
          </Button>
          <h1 className="mb-2 fw-bold" style={{ fontSize: '1.875rem', color: '#1e293b' }}>
            {stats.ambassador.name}
          </h1>
          <p className="text-muted mb-0 small">
            {stats.ambassador.email}
            {stats.ambassador.universityName && ` • ${stats.ambassador.universityName}`}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-2">Total Users Onboarded</CardTitle>
              <h2 className="fw-bold mb-0" style={{ fontSize: '2.5rem', color: '#1e293b' }}>
                {stats.totalUsersOnboarded}
              </h2>
            </CardBody>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-2">Performance Trend</CardTitle>
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    fontSize: '1.5rem',
                    color: getPerformanceColor(stats.performance.trend),
                    fontWeight: 'bold'
                  }}
                >
                  {getPerformanceIcon(stats.performance.trend)}
                </span>
                <div>
                  <h5 className="mb-0" style={{ color: getPerformanceColor(stats.performance.trend) }}>
                    {stats.performance.description}
                  </h5>
                  {stats.performance.changePercent !== 0 && (
                    <small className="text-muted">
                      {stats.performance.changePercent > 0 ? '+' : ''}{stats.performance.changePercent}% change
                    </small>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-2">Verification Pending</CardTitle>
              <h2 className="fw-bold mb-0" style={{ fontSize: '2.5rem', color: '#EA580C' }}>
                {stats.verificationPending || 0}
              </h2>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row className="mb-4">
        <Col md={6} className="mb-4">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h5" className="mb-3">Last 7 Days</CardTitle>
              {stats.last7Days && stats.last7Days.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={stats.last7Days}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#4C1D95" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      name="Users Onboarded"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted text-center py-4">No data available for the last 7 days</p>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col md={6} className="mb-4">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h5" className="mb-3">Last 4 Weeks</CardTitle>
              {stats.last4Weeks && stats.last4Weeks.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={stats.last4Weeks}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="week" 
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#4C1D95" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      name="Users Onboarded"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted text-center py-4">No data available for the last 4 weeks</p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Referred Users Table */}
      <Card className="shadow-sm border-0">
        <CardBody>
          <CardTitle tag="h5" className="mb-3">Referred Users</CardTitle>
          {usersLoading ? (
            <div className="d-flex justify-content-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : referredUsers.length === 0 ? (
            <p className="text-muted text-center py-4">No referred users found</p>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '10%' }}>S.No</th>
                      <th style={{ width: '40%' }}>Name</th>
                      <th style={{ width: '25%' }} className="text-center">Verified</th>
                      <th style={{ width: '25%' }}>Date of Joining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referredUsers.map((user) => (
                      <tr key={user.id}>
                        <td>{user.serialNumber}</td>
                        <td className="fw-medium">{user.name}</td>
                        <td className="text-center">
                          <span
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              backgroundColor: user.verified ? '#d1fae5' : '#fee2e2',
                              color: user.verified ? '#065f46' : '#991b1b',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                          >
                            {user.verified ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>{user.dateOfJoiningFormatted}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pagination && pagination.totalPages > 1 && (
                <div className="mt-3">
                  <Pagination pagination={pagination} onPageChange={handlePageChange} />
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default AmbassadorDetail;

